import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  IconButton,
  Collapse,
  FormControlLabel,
  Switch,
  Slider,
  TextField,
  Button,
} from "@material-ui/core";
import {
  ExpandMore as ExpandIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import useBDocTranslator from "../../hooks/useBDocTranslator";
import { TOOTH_STATUSES } from "./DentalChartUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(1, 0),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  filterContent: {
    paddingTop: theme.spacing(2),
  },
  filterRow: {
    marginBottom: theme.spacing(2),
  },
  activeFilters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  sliderContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const QUADRANT_OPTIONS = [
  { value: "I", label: "Cuadrante I (Superior Derecho)" },
  { value: "II", label: "Cuadrante II (Superior Izquierdo)" },
  { value: "III", label: "Cuadrante III (Inferior Izquierdo)" },
  { value: "IV", label: "Cuadrante IV (Inferior Derecho)" },
  { value: "V", label: "Cuadrante V (Deciduos Superior Derecho)" },
  { value: "VI", label: "Cuadrante VI (Deciduos Superior Izquierdo)" },
  { value: "VII", label: "Cuadrante VII (Deciduos Inferior Izquierdo)" },
  { value: "VIII", label: "Cuadrante VIII (Deciduos Inferior Derecho)" },
];

const TOOTH_TYPE_OPTIONS = [
  { value: "permanent", label: "Permanentes" },
  { value: "deciduous", label: "Deciduos" },
];

function DentalChartFilters({
  onFiltersChange,
  initialFilters = {},
  showAdvanced = false,
}) {
  const classes = useStyles();
  const { t } = useBDocTranslator();

  const [expanded, setExpanded] = useState(false);
  const [filters, setFilters] = useState({
    searchTerm: "",
    selectedStatuses: [],
    selectedQuadrants: [],
    toothType: "",
    toothRange: [1, 48],
    showOnlyProblematic: false,
    showOnlyTreated: false,
    ...initialFilters,
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleStatusToggle = (status) => {
    const newStatuses = filters.selectedStatuses.includes(status)
      ? filters.selectedStatuses.filter((s) => s !== status)
      : [...filters.selectedStatuses, status];
    handleFilterChange("selectedStatuses", newStatuses);
  };

  const handleQuadrantToggle = (quadrant) => {
    const newQuadrants = filters.selectedQuadrants.includes(quadrant)
      ? filters.selectedQuadrants.filter((q) => q !== quadrant)
      : [...filters.selectedQuadrants, quadrant];
    handleFilterChange("selectedQuadrants", newQuadrants);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      searchTerm: "",
      selectedStatuses: [],
      selectedQuadrants: [],
      toothType: "",
      toothRange: [1, 48],
      showOnlyProblematic: false,
      showOnlyTreated: false,
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.searchTerm) count++;
    if (filters.selectedStatuses.length > 0) count++;
    if (filters.selectedQuadrants.length > 0) count++;
    if (filters.toothType) count++;
    if (filters.toothRange[0] !== 1 || filters.toothRange[1] !== 48) count++;
    if (filters.showOnlyProblematic) count++;
    if (filters.showOnlyTreated) count++;
    return count;
  };

  const renderActiveFilters = () => {
    const activeFilters = [];

    if (filters.searchTerm) {
      activeFilters.push(
        <Chip
          key="search"
          label={`${t("Búsqueda")}: ${filters.searchTerm}`}
          onDelete={() => handleFilterChange("searchTerm", "")}
          size="small"
        />
      );
    }

    filters.selectedStatuses.forEach((status) => {
      activeFilters.push(
        <Chip
          key={`status-${status}`}
          label={`${t("Estado")}: ${TOOTH_STATUSES[status]?.label || status}`}
          onDelete={() => handleStatusToggle(status)}
          size="small"
          style={{
            backgroundColor: TOOTH_STATUSES[status]?.fill || "#default",
            color: "#fff",
          }}
        />
      );
    });

    filters.selectedQuadrants.forEach((quadrant) => {
      const quadrantOption = QUADRANT_OPTIONS.find((q) => q.value === quadrant);
      activeFilters.push(
        <Chip
          key={`quadrant-${quadrant}`}
          label={`${t("Cuadrante")}: ${quadrantOption?.label || quadrant}`}
          onDelete={() => handleQuadrantToggle(quadrant)}
          size="small"
        />
      );
    });

    if (filters.toothType) {
      const typeOption = TOOTH_TYPE_OPTIONS.find(
        (t) => t.value === filters.toothType
      );
      activeFilters.push(
        <Chip
          key="toothType"
          label={`${t("Tipo")}: ${typeOption?.label || filters.toothType}`}
          onDelete={() => handleFilterChange("toothType", "")}
          size="small"
        />
      );
    }

    if (filters.showOnlyProblematic) {
      activeFilters.push(
        <Chip
          key="problematic"
          label={t("Solo Problemáticos")}
          onDelete={() => handleFilterChange("showOnlyProblematic", false)}
          size="small"
          color="secondary"
        />
      );
    }

    if (filters.showOnlyTreated) {
      activeFilters.push(
        <Chip
          key="treated"
          label={t("Solo Tratados")}
          onDelete={() => handleFilterChange("showOnlyTreated", false)}
          size="small"
          color="primary"
        />
      );
    }

    return activeFilters;
  };

  return (
    <Card className={classes.root}>
      <CardContent>
        <div className={classes.header} onClick={() => setExpanded(!expanded)}>
          <Typography variant="subtitle1" component="div">
            <FilterIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
            {t("Filtros de Odontograma")}
            {getActiveFilterCount() > 0 && (
              <Chip
                label={getActiveFilterCount()}
                size="small"
                color="primary"
                style={{ marginLeft: 8 }}
              />
            )}
          </Typography>
          <IconButton size="small">
            <ExpandIcon
              style={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
              }}
            />
          </IconButton>
        </div>

        {/* Filtros activos siempre visibles */}
        {getActiveFilterCount() > 0 && (
          <Box className={classes.activeFilters}>
            {renderActiveFilters()}
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
            >
              {t("Limpiar Todo")}
            </Button>
          </Box>
        )}

        <Collapse in={expanded}>
          <div className={classes.filterContent}>
            <Grid container spacing={2}>
              {/* Búsqueda de texto */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("Buscar diente o notas")}
                  value={filters.searchTerm}
                  onChange={(e) =>
                    handleFilterChange("searchTerm", e.target.value)
                  }
                  InputProps={{
                    startAdornment: <SearchIcon style={{ marginRight: 8 }} />,
                  }}
                  size="small"
                />
              </Grid>

              {/* Tipo de diente */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("Tipo de Diente")}</InputLabel>
                  <Select
                    value={filters.toothType}
                    onChange={(e) =>
                      handleFilterChange("toothType", e.target.value)
                    }
                  >
                    <MenuItem value="">
                      <em>{t("Todos")}</em>
                    </MenuItem>
                    {TOOTH_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Estados de dientes */}
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  {t("Estados de Dientes")}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(TOOTH_STATUSES).map(([status, config]) => (
                    <Chip
                      key={status}
                      label={config.label}
                      onClick={() => handleStatusToggle(status)}
                      color={
                        filters.selectedStatuses.includes(status)
                          ? "primary"
                          : "default"
                      }
                      variant={
                        filters.selectedStatuses.includes(status)
                          ? "default"
                          : "outlined"
                      }
                      size="small"
                      style={{
                        backgroundColor: filters.selectedStatuses.includes(
                          status
                        )
                          ? config.fill
                          : "transparent",
                        color: filters.selectedStatuses.includes(status)
                          ? "#fff"
                          : config.fill,
                      }}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Cuadrantes */}
              <Grid item xs={12}>
                <Typography variant="body2" gutterBottom>
                  {t("Cuadrantes")}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {QUADRANT_OPTIONS.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => handleQuadrantToggle(option.value)}
                      color={
                        filters.selectedQuadrants.includes(option.value)
                          ? "primary"
                          : "default"
                      }
                      variant={
                        filters.selectedQuadrants.includes(option.value)
                          ? "default"
                          : "outlined"
                      }
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>

              {/* Rango de dientes */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  {t("Rango de Dientes")}: {filters.toothRange[0]} -{" "}
                  {filters.toothRange[1]}
                </Typography>
                <div className={classes.sliderContainer}>
                  <Slider
                    value={filters.toothRange}
                    onChange={(e, newValue) =>
                      handleFilterChange("toothRange", newValue)
                    }
                    valueLabelDisplay="auto"
                    min={1}
                    max={85}
                    marks={[
                      { value: 1, label: "1" },
                      { value: 48, label: "48" },
                      { value: 85, label: "85" },
                    ]}
                  />
                </div>
              </Grid>

              {/* Switches de filtros rápidos */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.showOnlyProblematic}
                      onChange={(e) =>
                        handleFilterChange(
                          "showOnlyProblematic",
                          e.target.checked
                        )
                      }
                      color="secondary"
                    />
                  }
                  label={t("Solo dientes problemáticos")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.showOnlyTreated}
                      onChange={(e) =>
                        handleFilterChange("showOnlyTreated", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label={t("Solo dientes tratados")}
                />
              </Grid>
            </Grid>

            {/* Botones de acción */}
            <div className={classes.actionButtons}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                disabled={getActiveFilterCount() === 0}
              >
                {t("Limpiar Filtros")}
              </Button>
            </div>
          </div>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default DentalChartFilters;
